/**
 * The helper settings, described once: Settings and the new-player helpers
 * page (docs/WORDS_AND_SENTENCES.md, part 3) both read these, so a helper
 * explains itself the same way wherever it is found.
 *
 * Pure data: no Phaser.
 */
import type { ProfileSettings } from '../systems/SaveManager';

export type HelperIcon = 'keyboard' | 'list' | 'enter' | 'music' | 'sound' | 'calm';

export interface Helper {
  readonly key: keyof ProfileSettings;
  readonly label: string;
  readonly help: string;
  /** Stored the other way round: the setting is "muted", the helper is "on". */
  readonly inverted: boolean;
  /** Only drawn on the wide stage; a phone has its own keyboard and no room for the list. */
  readonly wideOnly: boolean;
  readonly icon: HelperIcon;
}

export const HELPERS: readonly Helper[] = [
  {
    key: 'keyboardHints',
    label: 'Keyboard hints',
    help: 'Shows which finger and row the next letter belongs to.',
    inverted: false,
    wideOnly: true,
    icon: 'keyboard',
  },
  {
    key: 'wordList',
    label: 'Word list',
    help: 'Lists every word in the level down the left side, so you can read ahead.',
    inverted: false,
    wideOnly: true,
    icon: 'list',
  },
  {
    key: 'autoEnter',
    label: 'Auto enter',
    help: 'Jump as soon as the word is typed right. No need to press Enter.',
    inverted: false,
    wideOnly: false,
    icon: 'enter',
  },
  {
    key: 'musicMuted',
    label: 'Music',
    help: 'A calm backing track that changes as you climb.',
    inverted: true,
    wideOnly: false,
    icon: 'music',
  },
  {
    key: 'sfxMuted',
    label: 'Sound effects',
    help: 'Key clicks, jumps, dings and chimes.',
    inverted: true,
    wideOnly: false,
    icon: 'sound',
  },
  {
    key: 'reducedMotion',
    label: 'Reduced motion',
    help: 'Turns off screen shake and the big particle bursts.',
    inverted: false,
    wideOnly: false,
    icon: 'calm',
  },
];

export function helperFor(key: keyof ProfileSettings): Helper {
  const helper = HELPERS.find((h) => h.key === key);
  if (helper === undefined) throw new Error(`No helper for ${key}`);
  return helper;
}

/** Whether a helper is on, given the stored settings. */
export function helperOn(helper: Helper, settings: ProfileSettings): boolean {
  const value = settings[helper.key];
  return helper.inverted ? !value : value === true;
}
